import React, { useState, useRef, useEffect } from 'react';
import './AccountInfoForm.css';
import { getSession, getAutoToken, clearSession } from '../utils/authUtils';
import api from '../services/api';
import GMBService from '../services/GMBService';
import GMBWebsiteService from '../services/GMBWebsiteService';

import logger from '../utils/logger';
// Helper to format YYYY-MM-DD
const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const initialFormState = {
  name: '',
  business_name: '',
  cro_employee_name: '',
  seo_employee_name: '',
  contact_person: '',
  contact_number: '',
  email: '',
  address: '',
  website: '',
};

const AccountInfoForm = () => {
  const [form, setForm] = useState(initialFormState);
  const [, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const fileInputRef = useRef(null);
  const [accounts, setAccounts] = useState([]);
  const [locations, setLocations] = useState([]);

  // Fetch GMB data and compute defaults
  useEffect(() => {

    // Auto-fetch GMB account data
    const fetchGMBData = async () => {
      try {
        const session = getSession();
        const user = session?.user || session || {};

        // Try to get GMB data from session first
        const sessionValues = {
          business_name: user?.business_name || user?.company || session?.business_name || session?.gmbPrimaryLocationTitle || session?.gmbPrimaryLocation?.title || session?.gmbData?.locations?.[0]?.title || session?.locations?.[0]?.title || session?.gmbLocations?.[0]?.title || '',
          contact_person: user?.gmbPrimaryAccountName || session?.gmbPrimaryAccountName || user?.accountName || session?.accountName || (session?.gmbAccounts?.[0]?.accountName) || '',
          address: user.address || session?.address || '',
          email: user.email || session?.email || '',
          contact_number: user.contact_number || user.phone || session?.phone || session?.contact_number || '',
          website: user.website || session?.website || '',
          name: user?.gmbPrimaryAccountName || session?.gmbPrimaryAccountName || user?.accountName || session?.accountName || (session?.gmbAccounts?.[0]?.accountName) || '',
        };

        // Update form with session values
        setForm(prev => ({
          ...prev,
          name: sessionValues.name || prev.name,
          business_name: sessionValues.business_name || prev.business_name,
          contact_person: sessionValues.contact_person || prev.contact_person,
          address: sessionValues.address || prev.address,
          email: sessionValues.email || prev.email,
          contact_number: sessionValues.contact_number || prev.contact_number,
          website: sessionValues.website || prev.website,
        }));

        // If we still need more data, try to fetch from GMB API
        const token = getAutoToken();
        if (!token) return;

        // Fetch GMB accounts
        const accs = await GMBService.getAccounts(token);
        setAccounts(accs || []);

        if (accs && accs.length > 0) {
          // Fetch locations for the first account
          const locs = await GMBService.getLocations(token, accs[0].name);
          setLocations(locs || []);

          // If we have locations and need to fill in missing data
          if (locs && locs.length > 0 && (!sessionValues.business_name || !sessionValues.address || !sessionValues.contact_number)) {
            const primaryLocation = locs[0];
            const gmbAddress = primaryLocation?.storefrontAddress || primaryLocation?.address || {};
            const gmbPhone = primaryLocation?.phoneNumbers?.primaryPhone || primaryLocation?.primaryPhone || '';
            const gmbName = primaryLocation?.title || primaryLocation?.name || '';



            // Update form with GMB data
            setForm(prev => ({
              ...prev,
              business_name: gmbName || prev.business_name,
              address: (typeof gmbAddress === 'string' ? gmbAddress : (gmbAddress.addressLines ? gmbAddress.addressLines.join(', ') : '')) || prev.address,
              contact_person: gmbPhone || prev.contact_person,
              website: primaryLocation?.websiteUri || prev.website
            }));
          }
        }
      } catch (err) {
        logger.debug('[AccountInfoForm] GMB data fetch error', err);
      }
    };

    fetchGMBData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    setLogo(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setLogoPreview(null);
    }
  };
  const handleCancel = async (e) => {
    e.preventDefault();
    setForm(initialFormState);
    setLogo(null);
    setLogoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Get session and token using centralized utilities
    const session = getSession();
    const authToken = getAutoToken();

    // Debug token information
    logger.log('[AccountInfoForm] Token Debug Info:', {
      hasSession: !!session,
      hasToken: !!authToken,
      sessionId: session?.id || session?.user?.id,
      tokenPreview: authToken ? `${authToken.substring(0, 10)}...` : 'No token'
    });

    // Check for authentication
    if (!session || !authToken) {
      logger.error('[AccountInfoForm] Missing authentication data');
      alert('Authentication required. Please log in again.');
      clearSession();
      // Note: Navigation would need to be implemented if this component is within a router context
      return;
    }

    const payload = {
      user_id: session?.id || session?.user?.id,
      name: form.name,
      business_name: form.business_name,
      cro_employee_name: form.cro_employee_name,
      seo_employee_name: form.seo_employee_name,
      contact_person: form.contact_person,
      contact_number: form.contact_number,
      email: form.email,
      address: form.address,
      website: form.website
    };

    try {
      logger.log('[AccountInfoForm] Submitting account info...');

      // Use centralized API service with automatic token injection
      const response = await api.post('/customer/accounts/create-account', payload, { noAuth: true, noAutoLogout: true });
      logger.log('[AccountInfoForm] API Response:', response);
      alert('Updated!');
    } catch (error) {
      logger.error('[AccountInfoForm] API Error:', error);

      if (error.status === 401) {
        // Handle authentication error
        logger.log('[AccountInfoForm] Authentication failed, clearing session');
        clearSession();
        alert('Your session has expired. Please log in again.');
        // Note: Navigation would need to be implemented if this component is within a router context
      } else if (error.status === 400) {
        // Handle bad request with specific feedback
        const errorMessage = error.body?.message || error.body?.error || 'Invalid request data. Please check your information and try again.';
        logger.error('[AccountInfoForm] Validation error:', errorMessage);
        alert(`Error: ${errorMessage}`);
      } else {
        logger.error('[AccountInfoForm] Unexpected error:', error.message);
        alert('Something went wrong while submitting the form. Please try again.');
      }
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  return (
    <div className="account-info-form-container">
      <h2>Account Information</h2>
      <p>Manage your account details to personalize your business profile.</p>
      
      <div className="account-info-logo-section">
        <div className="account-info-logo">
          {logoPreview ? (
            <img src={logoPreview} alt="Logo Preview" />
          ) : (
            form.business_name?.charAt(0)?.toUpperCase() || form.name?.charAt(0)?.toUpperCase() || 'B'
          )}
        </div>
        <div className="logo-info">
          <div className="logo-info-title">Profile Logo</div>
          <div className="logo-info-desc">Upload a logo for your business profile</div>
          <label className="update-logo-btn">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            Upload Image
            <input
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleLogoChange}
              ref={fileInputRef}
            />
          </label>
        </div>
      </div>

      <form className="account-info-form" onSubmit={handleSubmit}>
        <div className="form-section">
          <div className="form-section-title">Basic Information</div>
          <label>
            Name <span className="required-indicator">*</span>
            <input name="name" value={form.name} onChange={handleChange} placeholder="Enter your name" />
          </label>
        </div>

        <label>
          Business Name
          <input name="business_name" value={form.business_name} onChange={handleChange} placeholder="Enter business name" />
        </label>

        {/* Start/End dates removed per request */}

        <div className="form-section">
          <div className="form-section-title">Contact Details</div>
        </div>

        <label>
          Email <span className="required-indicator">*</span>
          <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="email@example.com" />
        </label>

        <label>
          Contact Number
          <input name="contact_person" value={form.contact_person} onChange={handleChange} placeholder="+91 XXXXXXXXXX" />
        </label>

        <label>
          Address <span className="required-indicator">*</span>
          <input name="address" value={form.address} onChange={handleChange} placeholder="Enter your business address" />
        </label>

        <label>
          Website <span className="required-indicator">*</span>
          <input name="website" value={form.website} onChange={handleChange} placeholder="https://yourwebsite.com" />
        </label>

        <div className="form-section">
          <div className="form-section-title">Account Manager</div>
        </div>

        <div className="form-row">
          <label>
            Relationship Manager
            <input name="cro_employee_name" value={form.cro_employee_name} onChange={handleChange} placeholder="Manager name" />
          </label>
          <label>
            Manager Contact
            <input name="contact_number" value={form.contact_number} onChange={handleChange} placeholder="Phone number" />
          </label>
        </div>

        <div className="form-actions">
          <button type="button" className="cancel-btn" onClick={handleCancel}>Reset</button>
          <button type="submit" className="update-btn">Save Changes</button>
        </div>
      </form>
    </div>
  );
};

export default AccountInfoForm;